import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
} from "reactstrap";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import { connect } from "react-redux";
import loadingAction from "../../core/redux/actions/loadingAction";
import ReactTable from "react-table";
import TablePagination from "components/Table/TablePagination";
import strings from "../../core/translate";
import RowActions, {
  editAction,
  deleteAction,
} from "components/Table/RowActions";
import SelectRow from "../../components/Select/SelectRow";
import { Scopes } from "../../shared/scopes.enum";
import GarageSectionHeader from "../../components/Garage/GarageSectionHeader";
import { FormModal } from "../../components/Form/FormKit";
import ServiceDetailForm from "./ServiceDetailForm";

class MerchantBranchServiceDetailList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      dataSource: [],
      merchants: [],
      branches: [],
      branchServices: [],
      selectedMerchant: "",
      selectedBranch: "",
      selectedService: "",
      branchName: "",
      canWrite: false,
      formOpen: false,
      editing: null,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const merchantIdFromStorage = sessionStorage.getItem("merchantId");
    const routeState = this.props.location.state || {};
    const { selectedBranch, selectedService } = this.props.match.params;
    // opened from a service of a garage, so both are fixed for this page
    const branchId =
      selectedBranch && selectedBranch !== ":selectedBranch"
        ? selectedBranch
        : "";
    const serviceId =
      selectedService && selectedService !== ":selectedService"
        ? selectedService
        : "";
    const selectedMerchant =
      merchantIdFromStorage || routeState.selectedMerchant || "";

    this.setState(
      {
        canWrite: this.hasPermission([
          Scopes.MERCHANTS_BRANCHES_SERVICES_WRITE,
        ]),
        selectedMerchant,
        selectedBranch: branchId,
        selectedService: serviceId,
        branchName: routeState.branchName || "",
      },
      () => {
        if (selectedMerchant) {
          this.fetchBranches();
        } else {
          this.fetchMerchants();
        }
        if (branchId) {
          this.fetchBranchServices();
        }
        if (serviceId) {
          this.callPage();
        } else {
          this.setState({ isLoading: false });
        }
      }
    );
  }

  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      const merchants = response.data.items.map((m) => ({
        value: m.id,
        label: m.name,
      }));
      this.setState({ isLoading: false, merchants });
      if (merchants.length === 1) {
        this.setState({ selectedMerchant: merchants[0].value }, () =>
          this.fetchBranches(),
        );
      }
    }
  };

  fetchBranches = async () => {
    const { selectedMerchant } = this.state;
    if (!selectedMerchant) return;
    const response = await this.engine.getItem(
      `merchants/${selectedMerchant}/branches`,
    );
    if (response && response.status === 200) {
      const branches = response.data.items.map((b) => ({
        value: b.id,
        label: b.name || b.address,
      }));
      this.setState((prev) => {
        const match = branches.find((b) => b.value === prev.selectedBranch);
        return {
          isLoading: false,
          branches,
          branchName: prev.branchName || (match ? match.label : ""),
        };
      });
    }
  };

  fetchBranchServices = async () => {
    const { selectedBranch } = this.state;
    if (!selectedBranch) return;
    const response = await this.engine.getItem(
      `merchants-branches/${selectedBranch}/services`,
    );
    if (response && response.status === 200) {
      this.setState({
        // a deleted service comes back as null and cannot take new details
        branchServices: response.data.items
          .filter((s) => s.service)
          .map((s) => ({
            value: s.id,
            label: s.service.name,
          })),
      });
    }
  };

  callPage = async () => {
    const { selectedService } = this.state;
    if (!selectedService) return;

    const { selectedBranch } = this.state;
    const response = await this.engine.getItem(
      `merchants-branches/${selectedBranch}/services/${selectedService}/details`,
    );
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        dataSource: response.data.items.map((item, key) => ({
          id: key,
          data: item,
          name: item.name,
          price: item.price,
          remark: item.remark || "-",
          created: new Date(item.createdAt),
          actions: this.handleActions(key, item),
        })),
      });
    }
    this.props.loadingAction(false);
  };

  handleDelete = async (valueid) => {
    if (window.confirm(strings.confirmdelete)) {
      this.props.loadingAction(true);
      const { selectedBranch, selectedService } = this.state;
      await this.engine.deleteItem(
        `merchants-branches/${selectedBranch}/services/${selectedService}/details`,
        valueid,
      );
      this.callPage();
      this.props.loadingAction(false);
    }
  };

  // add and edit open the same dialog over the list
  handleEdit = (index) => {
    this.setState({
      formOpen: true,
      editing: this.state.dataSource[index].data,
    });
  };

  handleCloseForm = () => this.setState({ formOpen: false, editing: null });

  handleSaved = () => {
    this.setState({ formOpen: false, editing: null }, this.callPage);
  };

  handleActions = (index, item) => (
    <RowActions
        actions={[
          editAction(() => this.handleEdit(index, item.id), this.state.canWrite),
          deleteAction(() => this.handleDelete(item.id), this.state.canWrite),
        ]}
      />
  );

  handleMerchantSelect = (event) => {
    const selectedMerchant = event.value;
    if (selectedMerchant !== this.state.selectedMerchant) {
      this.setState(
        {
          selectedMerchant,
          selectedBranch: "",
          selectedService: "",
          branches: [],
          branchServices: [],
          dataSource: [],
        },
        () => this.fetchBranches(),
      );
    }
  };

  handleBranchSelect = (event) => {
    const selectedBranch = event.value;
    const match = this.state.branches.find((b) => b.value === selectedBranch);
    this.setState(
      {
        selectedBranch,
        branchName: match ? match.label : "",
        selectedService: "",
        branchServices: [],
        dataSource: [],
      },
      () => this.fetchBranchServices(),
    );
  };

  handleServiceSelect = (event) => {
    const selectedService = event.value;
    const { selectedBranch, selectedMerchant, branchName } = this.state;
    this.setState({ selectedService, dataSource: [] }, () => {
      if (selectedBranch) {
        this.props.history.replace({
          pathname: `/admin/merchants-branches/${selectedBranch}/services/${selectedService}/details`,
          state: { selectedMerchant, selectedBranch, branchName },
        });
      }
      this.callPage();
    });
  };

  handleAdd = () => {
    const { selectedBranch, selectedService } = this.state;
    if (!selectedBranch) {
      alert(strings.select + " " + strings.garage);
      return;
    }
    if (!selectedService) {
      alert(strings.select + " " + strings.service);
      return;
    }
    this.setState({ formOpen: true, editing: null });
  };

  render() {
    const {
      dataSource,
      isLoading,
      merchants,
      branches,
      branchServices,
      selectedMerchant,
      selectedBranch,
      selectedService,
      branchName,
      canWrite,
      formOpen,
      editing,
    } = this.state;
    const isGarageScoped = Boolean(selectedBranch);

    const columns = [
      { Header: strings.name, accessor: "name" },
      { Header: strings.price, accessor: "price" },
      { Header: strings.remark, accessor: "remark" },
      {
        Header: strings.created,
        accessor: "created",
        Cell: (row) => row.value.toLocaleDateString(),
      },
      {
        Header: strings.actions,
        accessor: "actions",
        sortable: false,
        filterable: false,
      },
    ];

    return (
      <div className="content">
        <GarageSectionHeader
          branchId={selectedBranch}
          branchName={branchName}
          merchantId={selectedMerchant}
          active="services"
          history={this.props.history}
        />
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4" className="pull-left">
                  {isGarageScoped ? strings.branch_service_details : ""}
                </CardTitle>
                {canWrite && (
                  <Button
                    color="primary"
                    className="pull-right"
                    onClick={this.handleAdd}
                  >
                    {strings.add}
                  </Button>
                )}

                {!isGarageScoped &&
                  !sessionStorage.getItem("merchantId") &&
                  merchants.length !== 1 && (
                    <SelectRow
                      label={strings.merchant}
                      name="selectedMerchant"
                      defaultValue={selectedMerchant}
                      data={merchants}
                      changeInput={this.handleMerchantSelect}
                      placeholder={strings.select}
                    />
                  )}

                {!isGarageScoped && (
                  <SelectRow
                    key={selectedMerchant || "default"}
                    label={strings.garage}
                    name="selectedBranch"
                    defaultValue={selectedBranch}
                    data={branches}
                    changeInput={this.handleBranchSelect}
                    placeholder={strings.select}
                    viewonly={!selectedMerchant}
                  />
                )}

                <SelectRow
                  key={selectedBranch || "no-branch"}
                  label={strings.service}
                  name="selectedService"
                  defaultValue={selectedService}
                  data={branchServices}
                  changeInput={this.handleServiceSelect}
                  placeholder={strings.select}
                  viewonly={!selectedBranch}
                />
              </CardHeader>
              <CardBody>
                <ReactTable
                  previousText={strings.previous}
                  nextText={strings.next}
                  loadingText={strings.loading}
                  noDataText={strings.nodata}
                  pageText={strings.page}
                  data={dataSource}
                  defaultFilterMethod={this.filterCaseInsensitive}
                  filterable
                  loading={isLoading}
                  columns={columns}
                  defaultPageSize={10}
                  showPaginationTop={false}
                  showPaginationBottom
                  PaginationComponent={TablePagination}
                  className="-striped -highlight primary-pagination"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <FormModal
          isOpen={formOpen}
          onClose={this.handleCloseForm}
          icon="nc-icon nc-bullet-list-67"
          size="md"
          title={
            editing
              ? `${strings.edit} ${strings.branch_service_details}`
              : strings.add_service_detail
          }
          subtitle={branchName}
        >
          <ServiceDetailForm
            key={(editing && editing.id) || "new"}
            item={editing}
            branchId={selectedBranch}
            serviceId={selectedService}
            onSaved={this.handleSaved}
            onCancel={this.handleCloseForm}
          />
        </FormModal>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(MerchantBranchServiceDetailList);
